import { Box, Card, Flex, Input, Stack, Table, VStack } from "@chakra-ui/react"
import { type QueryClient, useQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { type ReactNode, useEffect, useState } from "react"
import { FiSearch } from "react-icons/fi"
import { z } from "zod"

import {
  ContentService,
  type MembershipPublic,
  OrganizationsService,
  type UserPublic,
  UsersService,
} from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { SubjectActionsMenu } from "@/components/Common/SubjectActionsMenu"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { PageShell, PageSkeleton } from "@/components/Layout"
import AddSubject from "@/components/Subjects/AddSubject"
import MergeSubjects from "@/components/Subjects/MergeSubjects"
import { InputGroup } from "@/components/ui/input-group"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination.tsx"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"

const subjectsSearchSchema = z.object({
  page: z.number().catch(1),
  q: z.string().optional(),
})

const PER_PAGE = 10

type SubjectsSearchState = z.infer<typeof subjectsSearchSchema>

function getSubjectsQueryOptions({
  page,
  search,
}: {
  page: number
  search?: string
}) {
  const normalizedSearch = search?.trim() ? search.trim() : undefined
  return {
    queryFn: () =>
      ContentService.listSubjects({
        skip: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
        search: normalizedSearch,
      }),
    queryKey: ["subjects", { page, search: normalizedSearch }],
  }
}

export const Route = createFileRoute("/_layout/subjects")({
  component: Subjects,
  validateSearch: (search) => subjectsSearchSchema.parse(search),
  beforeLoad: async ({ context }) => {
    const queryClient = (context as { queryClient: QueryClient }).queryClient
    try {
      const currentUser = await queryClient.fetchQuery<UserPublic>({
        queryKey: ["currentUser"],
        queryFn: UsersService.readUserMe,
      })

      if (currentUser.is_superuser) {
        return
      }

      const memberships = await queryClient.fetchQuery<MembershipPublic[]>({
        queryKey: ["myMemberships", currentUser.id],
        queryFn: () => OrganizationsService.getMyMemberships(),
      })

      const hasAdminAccess = memberships.some(
        (membership) => membership.role === "admin",
      )

      if (!hasAdminAccess) {
        throw redirect({ to: "/" })
      }
    } catch {
      throw redirect({ to: "/" })
    }
  },
})

function SubjectsTable() {
  const navigate = useNavigate({ from: Route.fullPath })
  const { page, q } = Route.useSearch()
  const [searchValue, setSearchValue] = useState(q ?? "")
  const normalizedRouteSearch = (q ?? "").trim()
  const searchParam = normalizedRouteSearch ? normalizedRouteSearch : undefined

  useEffect(() => {
    setSearchValue(q ?? "")
  }, [q])

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getSubjectsQueryOptions({ page, search: searchParam }),
    placeholderData: (prevData) => prevData,
  })

  const setPage = (newPage: number) =>
    navigate({
      search: (prev: SubjectsSearchState) => ({ ...prev, page: newPage }),
    })

  useEffect(() => {
    const handler = setTimeout(() => {
      const normalizedInput = searchValue.trim()
      if (normalizedInput === normalizedRouteSearch) {
        return
      }

      navigate({
        search: (prev: SubjectsSearchState) => ({
          ...prev,
          page: 1,
          q: normalizedInput ? normalizedInput : undefined,
        }),
      })
    }, 300)

    return () => {
      clearTimeout(handler)
    }
  }, [navigate, normalizedRouteSearch, searchValue])

  const subjects = data?.data ?? []
  const count = data?.count ?? 0

  let content: ReactNode

  if (isLoading) {
    content = <PageSkeleton variant="table" rows={5} />
  } else if (subjects.length === 0) {
    content = (
      <AppEmptyState
        title={emptyStateCopy.subjects.none.title}
        description={emptyStateCopy.subjects.none.description}
        icon={<FiSearch />}
        data-testid="subjects-empty-state"
      />
    )
  } else {
    content = (
      <>
        {/* Desktop */}
        <Box display={{ base: "none", md: "block" }}>
          <Table.Root
            size={{ base: "sm", md: "md" }}
            data-testid="subjects-list"
          >
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader w="40%">Name</Table.ColumnHeader>
                <Table.ColumnHeader w="30%">Created</Table.ColumnHeader>
                <Table.ColumnHeader w="10%">Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {subjects.map((subject) => (
                <Table.Row
                  key={subject.id}
                  opacity={isPlaceholderData ? 0.5 : 1}
                  data-testid="subject-card"
                >
                  <Table.Cell truncate maxW="40%">
                    {subject.name}
                  </Table.Cell>
                  <Table.Cell>
                    {new Date(subject.created_at).toLocaleDateString()}
                  </Table.Cell>
                  <Table.Cell width="10%">
                    <SubjectActionsMenu subject={subject} />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>

        {/* Mobile cards */}
        <VStack display={{ base: "flex", md: "none" }} gap={3} align="stretch">
          {subjects.map((subject) => (
            <Card.Root key={subject.id} opacity={isPlaceholderData ? 0.5 : 1}>
              <Card.Body>
                <Flex justify="space-between" align="center">
                  <Stack gap={0} flex="1" minW={0} mr={2}>
                    <Box truncate fontWeight="medium">
                      {subject.name}
                    </Box>
                    <Box fontSize="sm" color="fg.muted">
                      {new Date(subject.created_at).toLocaleDateString()}
                    </Box>
                  </Stack>
                  <SubjectActionsMenu subject={subject} />
                </Flex>
              </Card.Body>
            </Card.Root>
          ))}
        </VStack>

        <Flex justifyContent="flex-end" mt={4}>
          <PaginationRoot
            count={count}
            pageSize={PER_PAGE}
            page={page}
            onPageChange={({ page }) => setPage(page)}
          >
            <Flex>
              <PaginationPrevTrigger />
              <PaginationItems />
              <PaginationNextTrigger />
            </Flex>
          </PaginationRoot>
        </Flex>
      </>
    )
  }

  return (
    <VStack gap={4} align="stretch">
      <Flex
        gap={3}
        wrap="wrap"
        align="center"
        justify="flex-start"
        data-testid="subjects-toolbar"
      >
        <InputGroup
          flex="1"
          minW={{ base: "100%", md: "300px" }}
          maxW={{ base: "100%", md: "360px" }}
          startElement={<FiSearch />}
          startElementProps={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            h: "100%",
          }}
        >
          <Input
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Search subjects"
            data-testid="subject-search-input"
          />
        </InputGroup>
      </Flex>
      {content}
    </VStack>
  )
}

function Subjects() {
  const breadcrumbs = useRouteBreadcrumbs("subjects.index")
  const actions = (
    <Flex gap={2} wrap="wrap">
      <MergeSubjects />
      <AddSubject />
    </Flex>
  )

  return (
    <PageShell
      title="Subject Management"
      description="Create and organize the subjects available to your organization."
      breadcrumbs={breadcrumbs}
      actions={actions}
    >
      <SubjectsTable />
    </PageShell>
  )
}
